import React, { useMemo, useState } from "react";

import { Box, Chip, MenuItem, MenuList, Popover, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useCanCreateDeal, useModals, useNavigate, usePrompt } from "hooks";
import { useTaskDealsSelect } from "store";
import { IEmail, ISuggestion } from "store/types";

import { CreateDealContent } from "../../../Prompts/CreateDealContent";
import { useEmailsContext } from "../context/Email-context";
import { SelectDealContent } from "./SelectDealContent";

interface Props {
  email: IEmail;
  setSuggestionDeal: (suggestion: ISuggestion, emailId: number) => void;
}

export const Suggestion: React.FC<Props> = ({ email, setSuggestionDeal }) => {
  const canCreateDeal = useCanCreateDeal();
  const { onMoveWithAiExtraction } = useEmailsContext();
  const { emailInboxModal } = useModals();
  const navigate = useNavigate();
  const openPrompt = usePrompt();
  const deals = useTaskDealsSelect();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const isAssociated = Boolean(email.deal_id);

  const onSelectOtherDeal = async () => {
    const result = await openPrompt({
      Component: SelectDealContent,
      props: {
        email,
        onMoveWithAiExtraction
      }
    });
    if (result) {
      setSuggestionDeal(result, email.id);
    }
  };

  const onCreateNewDeal = async () => {
    const data = await openPrompt({
      Component: CreateDealContent
    });
    if (data) {
      setSuggestionDeal({ id: data?.dealId, deal: { id: data?.dealId, title: data?.dealTitle } }, email.id);
    }
  };

  const chipLabel = useMemo(() => {
    if (isAssociated) {
      return deals.find((deal) => deal.id === email.deal_id)?.title;
    }
    return email.suggestions?.[0]?.deal.title || "";
  }, [isAssociated, email.suggestions, email.deal_id, deals]);

  return (
    <Stack direction="row" gap="10px" alignItems="center">
      <Typography sx={{ fontSize: "12px" }}>{isAssociated ? "Associated:" : "Suggestion:"}</Typography>
      <MaterialTooltip title={chipLabel} isShow={Boolean(chipLabel)}>
        <span>
          <Chip
            disabled={isAssociated && !chipLabel}
            onClick={(e) => {
              e.stopPropagation();
              if (isAssociated) {
                navigate({ route: "dealEmail", params: { deal_id: email.deal_id, email_id: email.id } });
                emailInboxModal(null);
              } else {
                setAnchorEl(e.currentTarget);
              }
            }}
            label={chipLabel || "..."}
            sx={{
              height: "24px",
              maxWidth: "150px",
              cursor: "pointer",
              backgroundColor: "#F6F8FC",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.08)",
                transform: "scale(1.02)",
                transition: "all 0.2s ease-in-out"
              },
              // disable background color if associated
              ...(isAssociated && {
                backgroundColor: "#DBE8F5"
              })
            }}
          />
        </span>
      </MaterialTooltip>
      <Popover
        open={!!anchorEl}
        onClose={(e: any) => {
          e.stopPropagation();
          setAnchorEl(null);
        }}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: "300px",
            maxHeight: "300px",
            borderRadius: "10px",
            backgroundColor: "#fff"
          }}
        >
          <MenuList
            sx={{
              padding: "8px",
              maxHeight: "200px",
              overflowY: "auto"
            }}
          >
            {email?.suggestions?.map((suggestionItem) => (
              <MenuItem
                key={suggestionItem.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setSuggestionDeal(suggestionItem, email.id);
                  setAnchorEl(null);
                }}
                sx={{
                  minHeight: "40px",
                  padding: "10px 12px",
                  borderRadius: "4px",
                  fontSize: "14px",
                  lineHeight: "20px",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  ...(suggestionItem.id === email.suggestions?.[0]?.id && {
                    backgroundColor: "#DBE8F5"
                  }),
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)"
                  }
                }}
              >
                {suggestionItem.deal.title}
              </MenuItem>
            ))}
          </MenuList>
          <MenuList sx={{ padding: "0 8px 8px 8px" }}>
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                onSelectOtherDeal();
                setAnchorEl(null);
              }}
              sx={{
                minHeight: "40px",
                padding: "10px 12px",
                borderRadius: "4px",
                fontSize: "14px",
                lineHeight: "20px",
                whiteSpace: "normal",
                wordBreak: "break-word",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)"
                }
              }}
            >
              Select Other Deal
            </MenuItem>
            {canCreateDeal && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateNewDeal();
                  setAnchorEl(null);
                }}
                sx={{
                  minHeight: "40px",
                  padding: "10px 12px",
                  borderRadius: "4px",
                  fontSize: "14px",
                  lineHeight: "20px",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)"
                  }
                }}
              >
                Add Deal
              </MenuItem>
            )}
          </MenuList>
        </Box>
      </Popover>
    </Stack>
  );
};
